
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import httpClient from "@/store/httpClient";
import DatePicker from "@/components/DatePicker.vue";
const Chart = require("chart.js");
import moment from "moment";

function groupBy(list: any, keyGetter: any) {
	const map = new Map();
	list.forEach((item: any) => {
		const key = keyGetter(item);
		const collection = map.get(key);
		if (!collection) {
			map.set(key, [item]);
		} else {
			collection.push(item);
		}
	});
	const array = Array.from(map, ([key, values]) => ({ key, values }));
	return array;
}

@Component({
	components: {
		DatePicker,
	},
})
export default class OrderedMaterialsReport extends Vue {
	private chart: any = null;
	private orderedMaterials: any = [];
	private loading: boolean = false;
	private groupByWerkstoffe: boolean = true;
	private startDate: string = moment().format("YYYY-01-01");
	private reportedType: string = "weight";
	private endDate: string = moment(moment().format("YYYY-01-01"))
		.add(1, "years")
		.subtract(1, "days")
		.format("YYYY-MM-DD");

	private mounted() {
		this.loadReport();
	}

	private setStartDate(date: Date) {
		this.startDate = moment(date).format("YYYY-MM-DD");
	}
	private setEndDate(date: Date) {
		this.endDate = moment(date).format("YYYY-MM-DD");
	}

	@Watch("startDate")
	@Watch("endDate")
	@Watch("groupByWerkstoffe")
	@Watch("reportedType")
	private async loadReport() {
		try {
			this.loading = true;
			const response = await httpClient().get(
				`Report/GetOrderedMaterialReport?from=${this.startDate}&to=${this.endDate}`
			);
			this.orderedMaterials = response.data.orderedMaterial;
			this.updateDiagram();
		} catch (err) {
			console.error("Loading report failed.");
			console.error(err);
			this.$store.commit(
				"setSnackbarErrorText",
				"Fehler beim Laden der Reports. Siehe Konsole."
			);
		} finally {
			this.loading = false;
		}
	}

	private getDatasets() {
		const colors = [
			"#264653",
			"#2A9D8F",
			"#E9C46A",
			"#F4A261",
			"#E76F51",
			"#6A994E",
			"#A7C957",
			"#F5CAC3",
			"#84A59D",
			"#F28482",
			"#F7E9A0",
			"#A8DADC",
			"#457B9D",
			"#1D3557",
			"#FF9F1C",
			"#2EC4B6",
			"#CBF3F0",
			"#FFBF69",
			"#ED6A5A",
			"#F2E2D2",
		];

		const datasets: any = [];
		let i = 0;
		if (this.groupByWerkstoffe) {
			for (const thickness of this.thicknessGroups) {
				const singleData = [
					...this.werkstoffGroups.map((g: any) =>
						g.values
							.filter((w: any) => w.thickness === thickness.key)
							.reduce((a: any, b: any) => {
								if (this.reportedType === "weight") {
									return a + Math.round(b.totalWeightInKg);
								} else if (this.reportedType === "positions") {
									return a + Math.round(b.numberOfPositions);
								}
							}, 0)
					),
				];
				datasets.push({
					label: thickness.key,
					data: singleData,
					backgroundColor: colors[i],
					stack: "Stack 0",
				});
				i++;
			}
		} else {
			for (const werkstoffGroup of this.werkstoffGroups) {
				const singleData = [
					...this.thicknessGroups.map((g: any) =>
						g.values
							.filter((w: any) => w.werkstoffName === werkstoffGroup.key)
							.reduce((a: any, b: any) => {
								if (this.reportedType === "weight") {
									return a + Math.round(b.totalWeightInKg);
								} else if (this.reportedType === "positions") {
									return a + Math.round(b.numberOfPositions);
								}
							}, 0)
					),
				];
				datasets.push({
					label: werkstoffGroup.key,
					data: singleData,
					backgroundColor: colors[i],
					stack: "Stack 0",
				});
				i++;
			}
		}

		return datasets;
	}

	private get werkstoffGroups() {
		return groupBy(this.orderedMaterials, (a: any) => a.werkstoffName).sort((a: any, b: any) =>
			a.key > b.key ? 1 : b.key > a.key ? -1 : 0
		);
	}

	private get thicknessGroups() {
		const thicknesses = groupBy(this.orderedMaterials, (m: any) => m.thickness);
		return thicknesses.sort((a: any, b: any) => a.key - b.key);
	}

	private updateDiagram() {
		let labels = this.werkstoffGroups.map((g: any) => g.key);
		if (!this.groupByWerkstoffe) {
			labels = this.thicknessGroups.map((t: any) => t.key);
		}

		// Destroy the chart if one already exists.
		if (this.chart != null) {
			this.chart.destroy();
		}

		const ctx = (this.$refs.diagramCanvas as any).getContext("2d");
		this.chart = new Chart(ctx, {
			// The type of chart we want to create
			type: "bar",
			// The data for our dataset
			data: {
				labels: labels,
				datasets: this.getDatasets(),
			},
			options: {
				plugins: {
					title: {
						display: true,
						text: "Bestellte Mengen",
					},
				},
				responsive: true,
				scales: {
					xAxes: [
						{
							stacked: true,
						},
					],
					yAxes: [
						{
							stacked: true,
						},
					],
				},
			},
		});

		this.chart.update();
	}
}
